import type { CatalogDto, LocalDto } from '@gl/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, Circle, QrCode, Rocket, UtensilsCrossed, Wallet, X, type LucideIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Spinner } from '@/components/FullScreenLoader';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { api, errorMessage } from '@/lib/api';
import { useHasRole } from '@/lib/local-context';
import { queryKeys } from '@/lib/query';
import { cn } from '@/lib/utils';
import { dismissFirstSteps, isFirstStepsDismissed, isQrDownloaded } from './first-steps-storage';

export interface FirstStepsState {
  hasCatalog: boolean;
  isPublished: boolean;
  qrDownloaded: boolean;
  hasSales: boolean;
}

/** Estado de cada paso a partir de los datos reales (catálogo, local, ventas) y del QR descargado. */
export function firstStepsState(
  local: Pick<LocalDto, 'isPublished' | 'salesCount'>,
  catalog: CatalogDto | undefined,
  todaySales: number,
  qrDownloaded: boolean,
): FirstStepsState {
  const hasCatalog = !!catalog?.categories.some((c) => c.isActive && c.items.some((i) => i.isActive));
  return {
    hasCatalog,
    isPublished: local.isPublished,
    qrDownloaded,
    hasSales: local.salesCount > 0 || todaySales > 0,
  };
}

/**
 * ¿Mostrar la checklist? Siempre que el catálogo esté vacío o el menú sin publicar; con eso
 * resuelto, mientras quede algo pendiente y no la hayan ocultado.
 */
export function shouldShowFirstSteps(s: FirstStepsState, dismissed: boolean): boolean {
  if (!s.hasCatalog || !s.isPublished) return true;
  return !dismissed && !(s.qrDownloaded && s.hasSales);
}

interface Step {
  key: keyof FirstStepsState;
  icon: LucideIcon;
  title: string;
  hint: string;
  to: string;
  cta: string;
}

const STEPS: Step[] = [
  {
    key: 'hasCatalog',
    icon: UtensilsCrossed,
    title: 'Cargá tu primera categoría y producto',
    hint: 'Por ejemplo “Helados” y adentro “1/4 kg” con su precio.',
    to: '../menu',
    cta: 'Ir al menú',
  },
  {
    key: 'isPublished',
    icon: Rocket,
    title: 'Publicá el menú',
    hint: 'Así cualquiera con el link o el QR lo puede ver.',
    to: '../configuracion/dominio',
    cta: 'Publicar',
  },
  {
    key: 'qrDownloaded',
    icon: QrCode,
    title: 'Descargá el QR',
    hint: 'Imprimilo para el mostrador y las mesas.',
    to: '../configuracion/dominio',
    cta: 'Ver el QR',
  },
  {
    key: 'hasSales',
    icon: Wallet,
    title: 'Cargá tu primera venta',
    hint: 'Desde la caja, en dos toques.',
    to: '../caja',
    cta: 'Ir a la caja',
  },
];

export function FirstSteps({
  local,
  catalog,
  todaySales,
  welcome,
}: {
  local: LocalDto;
  catalog: CatalogDto | undefined;
  todaySales: number;
  /** Recién creado desde el onboarding: título de bienvenida y scroll hasta acá. */
  welcome?: boolean;
}) {
  const isOwner = useHasRole('OWNER');
  const qc = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const [dismissed, setDismissed] = useState(() => isFirstStepsDismissed(local.id));
  const state = firstStepsState(local, catalog, todaySales, isQrDownloaded(local.id));

  useEffect(() => {
    if (welcome) ref.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [welcome]);

  const publish = useMutation({
    mutationFn: () => api.locals.publish(local.id, { published: true }),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.local(updated.id), updated);
      void qc.invalidateQueries({ queryKey: queryKeys.locals, exact: true });
      toast.success('¡Tu menú ya está publicado!');
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!catalog || !shouldShowFirstSteps(state, dismissed)) return null;

  const done = STEPS.filter((s) => state[s.key]).length;
  const next = STEPS.find((s) => !state[s.key]);
  const canDismiss = state.hasCatalog && state.isPublished;

  return (
    <Card ref={ref} className="scroll-mt-4 border-primary/30">
      <CardHeader className="flex items-start justify-between gap-3">
        <div className="grid gap-1">
          <CardTitle className="text-base">{welcome ? `¡Listo, ya creaste ${local.name}!` : 'Primeros pasos'}</CardTitle>
          <CardDescription>
            {done === 0 ? 'Cuatro pasos y tu local queda andando.' : `Llevás ${done} de ${STEPS.length}. ¡Vas bien!`}
          </CardDescription>
        </div>
        {canDismiss && (
          <Button
            variant="ghost"
            size="icon"
            className="-mt-1 -mr-2 size-8 text-muted-foreground"
            aria-label="Ocultar primeros pasos"
            onClick={() => {
              dismissFirstSteps(local.id);
              setDismissed(true);
            }}
          >
            <X />
          </Button>
        )}
      </CardHeader>
      <CardContent className="grid gap-3">
        <div className="h-1.5 overflow-hidden rounded-full bg-muted" aria-hidden>
          <div className="h-full bg-primary transition-[width]" style={{ width: `${(done / STEPS.length) * 100}%` }} />
        </div>
        <ol className="grid gap-2">
          {STEPS.map((step) => {
            const isDone = state[step.key];
            const isNext = step === next;
            const publishInline = step.key === 'isPublished' && isOwner && state.hasCatalog;
            return (
              <li
                key={step.key}
                className={cn(
                  'flex items-center gap-3 rounded-lg border p-3',
                  isNext && 'border-primary/50 bg-primary/5',
                  isDone && 'bg-muted/40',
                )}
              >
                {isDone ? (
                  <CheckCircle2 className="size-5 shrink-0 text-success" aria-label="Hecho" />
                ) : (
                  <Circle className="size-5 shrink-0 text-muted-foreground" aria-label="Pendiente" />
                )}
                <step.icon className="hidden size-5 shrink-0 text-muted-foreground sm:block" />
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-medium', isDone && 'text-muted-foreground line-through')}>{step.title}</p>
                  {!isDone && (
                    <p className="text-xs text-muted-foreground">
                      {step.key === 'isPublished' && !isOwner ? 'Pedile al dueño del local que lo publique.' : step.hint}
                    </p>
                  )}
                </div>
                {!isDone &&
                  (publishInline ? (
                    <Button
                      size="sm"
                      variant={isNext ? 'default' : 'outline'}
                      disabled={publish.isPending}
                      onClick={() => publish.mutate()}
                    >
                      {publish.isPending && <Spinner />} Publicar
                    </Button>
                  ) : (
                    <Button size="sm" variant={isNext ? 'default' : 'outline'} asChild>
                      <Link to={step.to}>
                        {step.cta} <ChevronRight className="hidden sm:block" />
                      </Link>
                    </Button>
                  ))}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
